import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Search, Filter, MoreVertical, Shield, Ban, CheckCircle, XCircle, User, Eye } from 'lucide-react';
import AdminNavbar from '../components/AdminNavbar';

const AdminUsersPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });

    useEffect(() => {
        fetchUsers();
    }, [pagination.page, search]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const offset = (pagination.page - 1) * pagination.limit;
            const response = await api.get(`/admin/users?limit=${pagination.limit}&offset=${offset}&search=${search}`);

            if (response.data && response.data.users) {
                setUsers(response.data.users);
                setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
            }
        } catch (error) {
            console.error('Fetch users error:', error);
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleBlockUser = async (userId, currentStatus) => {
        if (!window.confirm(`Are you sure you want to ${currentStatus === 'banned' ? 'unban' : 'ban'} this user?`)) return;

        try {
            // If banning, call block endpoint. If unbanning, update status to active
            const newStatus = currentStatus === 'banned' ? 'active' : 'banned';
            // Note: Adjust endpoint based on your specific backend implementation if needed. 
            // Based on routes seen: POST /users/:userId/block or PUT /users/:userId/status

            if (newStatus === 'banned') {
                await api.post(`/admin/users/${userId}/block`, { reason: 'Admin Action' });
            } else {
                await api.put(`/admin/users/${userId}/status`, { status: 'active' });
            }

            toast.success(`User ${newStatus === 'banned' ? 'banned' : 'unbanned'} successfully`);
            fetchUsers();
        } catch (error) {
            toast.error('Failed to update user status');
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 text-white">
            <AdminNavbar />

            <div className="container mx-auto px-6 py-8">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-2xl font-bold">User Management</h1>

                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white w-64"
                        />
                    </div>
                </div>

                <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-slate-700/50 text-gray-400 text-xs uppercase font-semibold">
                                <tr>
                                    <th className="px-6 py-4">User</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">KYC</th>
                                    <th className="px-6 py-4">Joined</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-8 text-center text-gray-400">Loading...</td>
                                    </tr>
                                ) : users.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-8 text-center text-gray-400">No users found</td>
                                    </tr>
                                ) : (
                                    users.map((user) => (
                                        <tr key={user.id} className="hover:bg-slate-700/30 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-white">{user.firstName} {user.lastName}</span>
                                                    <span className="text-sm text-gray-400">{user.email}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.status === 'active' ? 'bg-green-500/10 text-green-400' :
                                                    user.status === 'banned' ? 'bg-red-500/10 text-red-400' :
                                                        'bg-yellow-500/10 text-yellow-400'
                                                    }`}>
                                                    {user.status.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`flex items-center gap-1.5 text-sm ${user.kycStatus === 'verified' ? 'text-green-400' :
                                                    user.kycStatus === 'pending' ? 'text-yellow-400' :
                                                        'text-gray-400'
                                                    }`}>
                                                    {user.kycStatus === 'verified' ? <CheckCircle size={14} /> :
                                                        user.kycStatus === 'rejected' ? <XCircle size={14} /> :
                                                            <Shield size={14} />}
                                                    {user.kycStatus.charAt(0).toUpperCase() + user.kycStatus.slice(1)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-400">
                                                {new Date(user.createdAt).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 text-right flex justify-end gap-2">
                                                <Link
                                                    to={`/admin/users/${user.id}`}
                                                    className="p-2 rounded-lg hover:bg-slate-600 transition-colors text-blue-400"
                                                    title="View Details"
                                                >
                                                    <Eye size={18} />
                                                </Link>
                                                <button
                                                    onClick={() => handleBlockUser(user.id, user.status)}
                                                    className={`p-2 rounded-lg hover:bg-slate-600 transition-colors ${user.status === 'banned' ? 'text-green-400' : 'text-red-400'
                                                        }`}
                                                    title={user.status === 'banned' ? 'Unban User' : 'Ban User'}
                                                >
                                                    <Ban size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="px-6 py-4 border-t border-slate-700 flex justify-between items-center text-sm text-gray-400">
                        <span>Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users</span>
                        <div className="flex gap-2">
                            <button
                                disabled={pagination.page === 1}
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                className="px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white"
                            >
                                Previous
                            </button>
                            <button
                                disabled={pagination.page * pagination.limit >= pagination.total}
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                className="px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminUsersPage;
